/**
 * Host-side selector generation. Mirrors the priority order encoded in the
 * iframe runtime (`src/canvas-runtime/runtime.ts`) and the postmessage
 * contract (`contracts/postmessage.md` § Selector format):
 *
 *   1. `#id` if unique
 *   2. `tag[aria-label="…"]` if unique
 *   3. `tag[role="…"]` if unique
 *   4. structural `nth-of-type` chain from <body>
 *
 * The host needs its own copy because comment re-resolution against a new
 * version's HTML happens in the host, not the iframe (the new version's DOM
 * isn't necessarily mounted yet).
 */

export interface BuildSelectorOptions {
  /** When true and a unique text-content match exists, append it for stability. Default true. */
  preferText?: boolean;
}

function uniqueIn(scope: ParentNode, selector: string): boolean {
  try {
    return scope.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

function escapeAttr(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function buildSelector(el: Element, scope: ParentNode = el.ownerDocument!): string {
  if (!el || !el.tagName) return '';
  const tag = el.tagName.toLowerCase();

  if (el.id) {
    const byId = `#${CSS.escape ? CSS.escape(el.id) : el.id}`;
    if (uniqueIn(scope, byId)) return byId;
  }

  const aria = el.getAttribute('aria-label');
  if (aria) {
    const byLabel = `${tag}[aria-label="${escapeAttr(aria)}"]`;
    if (uniqueIn(scope, byLabel)) return byLabel;
  }

  const role = el.getAttribute('role');
  if (role) {
    const byRole = `${tag}[role="${escapeAttr(role)}"]`;
    if (uniqueIn(scope, byRole)) return byRole;
  }

  const parts: string[] = [];
  let cur: Element | null = el;
  const root = (scope as Document).body ?? scope;
  while (cur && cur !== root && cur.nodeType === 1) {
    const t = cur.tagName.toLowerCase();
    const parent: ParentNode | null = cur.parentNode;
    if (!parent) break;
    let idx = 1;
    for (const sibling of Array.from(parent.children)) {
      if (sibling === cur) break;
      if (sibling.tagName === cur.tagName) idx++;
    }
    parts.unshift(`${t}:nth-of-type(${idx})`);
    cur = cur.parentElement;
  }
  return ['body', ...parts].join(' > ');
}

export function resolves(html: string, selector: string): boolean {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector) !== null;
  } catch {
    return false;
  }
}

export function snippetFor(el: Element, max = 200): string {
  let s = '';
  try {
    s = el.outerHTML ?? '';
  } catch {
    s = '';
  }
  return s.length > max ? `${s.slice(0, max - 3)}...` : s;
}
